import { Link, createFileRoute } from "@tanstack/react-router";
import { PageImageGallery } from "@/components/page-image-gallery";

import { Card } from "@/components/ui/card";
import { AUTHORS } from "@/lib/authors";
import { ProseSection } from "@/components/prose-section";
import { TesterCoverageInfographic } from "@/components/infographics";
import { SITE } from "@/lib/site";
import { ogImageMeta } from "@/lib/og";

export const Route = createFileRoute("/auteurs/")({
  head: () => ({
    meta: [
      { title: "Redactie en Factcheckers achter Casino Betalingen" },
      {
        name: "description",
        content:
          "Maak kennis met de mensen die stortingen testen, cijfers controleren en elke betaalgids schrijven. Met achtergrond en expertise per auteur.",
      },
      { property: "og:title", content: "Redactie en Factcheckers achter Casino Betalingen" },
      {
        property: "og:description",
        content: "Maak kennis met de mensen die stortingen testen, cijfers controleren en elke betaalgids schrijven. Met achtergrond en expertise per auteur.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
      ...ogImageMeta("auteurs"),
      { property: "og:url", content: `${SITE.url}/auteurs/` },
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/auteurs/` }],
  }),
  component: AuthorsPage,
});

function AuthorsPage() {
  return (
    <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">Onze casino-experts</h1>
      <p className="mt-3 max-w-3xl text-lg text-muted-foreground">
        Elke pagina op {SITE.name} heeft een verantwoordelijke redacteur en een factchecker. Hieronder zie je wie welk
        onderwerp beheert, sinds wanneer en hoe de profielen op 1 november 2026 zijn hercontroleerd.
      </p>

      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {AUTHORS.map((author) => (
          <Card key={author.slug} className="card-surface p-5">
            <div className="flex items-center gap-3">
              <img src={author.photo} alt={author.name} loading="lazy" width={56} height={56} decoding="async" className="h-14 w-14 rounded-full object-cover" />
              <div>
                <p className="font-semibold text-foreground">{author.name}</p>
                <p className="text-xs text-muted-foreground">{author.role}</p>
              </div>
            </div>
            <p className="mt-4 line-clamp-4 text-sm leading-6 text-muted-foreground">{author.bio}</p>
            <p className="mt-3 text-xs text-muted-foreground">Specialisme: {author.expertise.join(", ")}</p>
          </Card>
        ))}
      </div>

      <div className="mt-12">
        <TesterCoverageInfographic />
      </div>

      <ProseSection id="werkwijze" title="Hoe onze redactie test en controleert">
        <p>
          Voor elke betaalmethode storten en verzoeken we zelf uitbetalingen bij de aanbieders in
          onze vergelijking. De metingen achter de pagina's over{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "ideal" }}>iDEAL</Link>,{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "trustly" }}>Trustly</Link> en{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "skrill" }}>Skrill</Link> komen uit die
          testreeks; elk artikel vermeldt de datum en de redacteur die de cijfers heeft
          gecontroleerd.
        </p>
        <p>
          Wijzigt een limiet, een kostenpost of een uitbetalingstijd, dan passen we de betreffende
          pagina aan en noteren we dat bij de laatste controle. Een volledig overzicht vind je bij{" "}
          <Link to="/betaalmethoden/">alle betaalmethoden</Link>; wil je meteen filteren op jouw
          methode, gebruik dan de <Link to="/casinozoeker/">casinozoeker</Link>.
        </p>
      </ProseSection>

          <PageImageGallery path="/auteurs" title="Onze redactie aan het werk" />
    </main>
  );
}
